import { apiFetch } from './apiFetch';

export type UserRole = 'manager' | 'user' | 'admin' | 'multiadmin';

export type PlatformRole = 'staff' | 'superadmin';

export type AccountStatus = 'active' | 'suspended' | 'trial';

export interface UserAccount {
  id: string;
  slug: string;
  name: string | null;
  status: AccountStatus;
}

export interface UserProfile {
  id: string;
  accountId: string | null;
  email: string | null;
  firstName: string | null;
  preferredName: string | null;
  languageSlug: string | null;
  shiftInfo: Record<string, unknown> | null;
  agentName: string | null;
  tone: string | null;
  onboardingCompletedAt: string | null;
  role: UserRole | null;
  // Added by the standalone contract; older API builds omit them.
  lastName?: string | null;
  platformRole?: PlatformRole | null;
  account?: UserAccount | null;
}

const USER_ROLES: readonly UserRole[] = ['manager', 'user', 'admin', 'multiadmin'];

export const normalizeUserRole = (raw: unknown): UserRole | null =>
  USER_ROLES.includes(raw as UserRole) ? (raw as UserRole) : null;

// Account admins (including legacy multi-account admins) get the manager experience in Khova.
const MANAGER_ROLES_ACCESS: readonly UserRole[] = ['manager', 'admin', 'multiadmin'];

export const hasManagerAccess = (user: UserProfile | null | undefined): boolean =>
  user?.role != null && MANAGER_ROLES_ACCESS.includes(user.role);

export const isUser = (user: UserProfile | null | undefined): boolean => user?.role === 'user';

export interface PutUserProfilePayload {
  preferredName?: string | null;
  languageId?: string | null;
  shiftInfo?: Record<string, unknown> | null;
  agentName?: string | null;
  tone?: string | null;
  onboardingCompletedAt?: string | null;
}

export const getDisplayName = (user: UserProfile): string => user.preferredName?.trim() || user.firstName?.trim() || '';

export const putUserProfile = async (userId: string, payload: PutUserProfilePayload): Promise<UserProfile> => {
  const res = await apiFetch(`/users/${userId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err?.error ?? res.statusText);
  }
  return (await res.json()) as UserProfile;
};
